import { useCallback } from 'react'

import { toast } from '@sales-center/components'

import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateLeadCompanyRequiredFields } from '@user/store/lead/api'
import { isAPIError } from '@user/store/services/crud'

import { buildCompanyRequiredFieldsInput } from '../utils/buildCompanyRequiredFieldsInput'
import type { UseData } from './useData'
import type { LeadCompanyRequiredFieldsFormValues } from './useInitialValues'

type Props = {
  leadId: number
  data: UseData
  onClose: () => void
  onSaved: () => void
}

const FALLBACK_ERROR = 'Failed to update required fields.'

export function useHandlers(props: Props) {
  const { leadId, data, onClose, onSaved } = props
  const { fields, extraKeys, lookups } = data

  const dispatch = useAppDispatch()

  const onSubmit = useCallback(
    async (values: LeadCompanyRequiredFieldsFormValues) => {
      const input = buildCompanyRequiredFieldsInput(
        values,
        fields,
        extraKeys,
        lookups,
      )
      const result = await dispatch(
        updateLeadCompanyRequiredFields({ leadId, input }),
      )

      if (updateLeadCompanyRequiredFields.rejected.match(result)) {
        toast.error(
          result.payload && isAPIError(result.payload)
            ? result.payload.Message
            : FALLBACK_ERROR,
        )
        return
      }

      onClose()
      onSaved()
    },
    [dispatch, leadId, fields, extraKeys, lookups, onClose, onSaved],
  )

  return { onSubmit }
}
